/**
 * Acid–base equilibrium at 25 °C, solved from the charge balance rather than by
 * approximation.
 *
 * ## Constants
 *
 * Kw = 1.0 × 10⁻¹⁴ at 25 °C (pKw = 14.00), the textbook value used by OpenStax Chemistry
 * 2e. Kw changes with temperature, so every result in these tools is a 25 °C result. Ka and
 * Kb are the 25 °C values in OpenStax Chemistry 2e Appendices H and I.
 *
 * ## Method
 *
 * For a solution with total concentrations of an acid or base and any spectator ions, the
 * charge balance
 *
 *     Σ[cations] + [H⁺] = Σ[anions] + [OH⁻]
 *
 * is one equation in [H⁺] alone once each weak species is written as a fraction of its
 * total (α = Ka ÷ (Ka + [H⁺]) for a weak acid's conjugate base). Its left side minus its
 * right side increases with [H⁺], so it has exactly one root, which `solveHydrogen` finds
 * by bisection on log₁₀[H⁺] to machine precision. No assumption that x is small, and water's
 * own ionisation is included, so even 10⁻⁸ M HCl comes out at pH 6.98, not 8.
 */

export const KW = 1.0e-14;
export const PKW = 14;

const LOG_LOW = -17;
const LOG_HIGH = 2;
const ITERATIONS = 200;

/**
 * Concentration of H⁺ (mol/L) at which `imbalance` is zero. `imbalance(h)` must be
 * negative for very small h and positive for very large h, and increase with h.
 */
export function solveHydrogen(imbalance: (h: number) => number): number {
  let low = LOG_LOW;
  let high = LOG_HIGH;
  for (let i = 0; i < ITERATIONS; i++) {
    const mid = (low + high) / 2;
    if (imbalance(10 ** mid) > 0) {
      high = mid;
    } else {
      low = mid;
    }
  }
  return 10 ** ((low + high) / 2);
}

export function pFrom(value: number): number {
  return -Math.log10(value);
}

export function fromP(p: number): number {
  return 10 ** -p;
}

/* ---------------------------------------------------------------- one solute */

export type SoluteKind =
  | "strong-acid"
  | "weak-acid"
  | "strong-base"
  | "weak-base";

export interface Solute {
  kind: SoluteKind;
  /** Ka of a weak acid, Kb of a weak base; ignored for strong ones. */
  k?: number;
}

export function isAcid(kind: SoluteKind): boolean {
  return kind === "strong-acid" || kind === "weak-acid";
}

/**
 * Fraction of the solute that carries charge at [H⁺] = h: the conjugate base of an acid
 * (A⁻), or the conjugate acid of a base (BH⁺). A strong solute is always fully charged.
 */
export function chargedFraction(solute: Solute, h: number): number {
  if (solute.kind === "weak-acid") {
    const ka = solute.k ?? 0;
    return ka / (ka + h);
  }
  if (solute.kind === "weak-base") {
    const kaOfConjugate = KW / (solute.k ?? 1);
    return h / (h + kaOfConjugate);
  }
  return 1;
}

export interface Mixture {
  solute: Solute;
  /** Total concentration of the solute in the final solution, mol/L. */
  concentration: number;
  /**
   * Concentration of the spectator ion that came with the *titrant*: Na⁺ for an acid
   * titrated with NaOH, or X⁻ (Cl⁻) for a base titrated with HCl. 0 when nothing was added.
   */
  titrantIon: number;
}

/**
 * pH of an acid or base, with or without a strong titrant added.
 *
 * Acid: [Na⁺] + [H⁺] = [OH⁻] + α·C. Base: α·C + [H⁺] = [OH⁻] + [Cl⁻].
 */
export function phOfMixture(mixture: Mixture): number {
  const { solute, concentration, titrantIon } = mixture;
  const acid = isAcid(solute.kind);
  const hydrogen = solveHydrogen((h) => {
    const charged = chargedFraction(solute, h) * concentration;
    return acid
      ? titrantIon + h - KW / h - charged
      : charged + h - KW / h - titrantIon;
  });
  return pFrom(hydrogen);
}

/* ------------------------------------------------- the textbook weak-acid route */

export interface WeakEquilibrium {
  /** x = [H⁺] (or [OH⁻]) from the full quadratic x² + K·x − K·C = 0. */
  quadratic: number;
  /** x ≈ √(K·C), the small-x approximation. */
  approximate: number;
  /** x ÷ C of the quadratic root: the "percent ionised". */
  fractionIonised: number;
  /** The approximation's x ÷ C, which is what the 5 % test looks at. */
  approximateFraction: number;
  /** Whether the small-x approximation passes the 5 % test (x ÷ C < 0.05). */
  fivePercentOk: boolean;
}

const FIVE_PERCENT = 0.05;

/**
 * The textbook route for a weak acid or base of concentration C and constant K:
 * x² + Kx − KC = 0, x = (−K + √(K² + 4KC)) ÷ 2, against the approximation x = √(KC).
 * Water's ionisation is ignored here, as in a textbook; `phOfMixture` includes it.
 */
export function weakEquilibrium(
  k: number,
  concentration: number
): WeakEquilibrium {
  const quadratic = (-k + Math.sqrt(k * k + 4 * k * concentration)) / 2;
  const approximate = Math.sqrt(k * concentration);
  const fractionIonised = quadratic / concentration;
  return {
    quadratic,
    approximate,
    fractionIonised,
    approximateFraction: approximate / concentration,
    fivePercentOk: approximate / concentration < FIVE_PERCENT,
  };
}
